export type ModelFamily = "sonnet" | "codex" | "gemini";
export type ProviderId = "anthropic" | "openai" | "google";

export interface ModelConfig {
  answerFamily: ModelFamily;
  parseFamily: ModelFamily;
  utilitiesOverrideFamily?: ModelFamily | null;
}

export interface ProviderKeyStatus {
  anthropic: boolean;
  openai: boolean;
  google: boolean;
}

export const DEFAULT_MODEL_CONFIG: ModelConfig = {
  answerFamily: "sonnet",
  parseFamily: "sonnet",
  utilitiesOverrideFamily: null,
};

export const EMPTY_PROVIDER_KEY_STATUS: ProviderKeyStatus = {
  anthropic: false,
  openai: false,
  google: false,
};

export const FAMILY_OPTIONS: Array<{ id: ModelFamily; label: string }> = [
  { id: "sonnet", label: "Sonnet" },
  { id: "codex", label: "Codex" },
  { id: "gemini", label: "Gemini" },
];

export const PROVIDER_LABELS: Record<ProviderId, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
};

export const PROVIDER_KEY_PLACEHOLDERS: Record<ProviderId, string> = {
  anthropic: "sk-ant-...",
  openai: "sk-...",
  google: "AIza...",
};

export const PROVIDER_KEY_DOCS: Record<ProviderId, string> = {
  anthropic: "https://console.anthropic.com/settings/keys",
  openai: "https://platform.openai.com/api-keys",
  google: "https://aistudio.google.com/app/apikey",
};

export const APPROVED_MODEL_CATALOG: Record<ModelFamily, Array<{ id: string; label: string }>> = {
  sonnet: [
    { id: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
    { id: "claude-opus-4-6", label: "Claude Opus 4.6" },
    { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
  ],
  codex: [
    { id: "gpt-5.2", label: "GPT-5.2" },
    { id: "gpt-4o", label: "GPT-4o" },
  ],
  gemini: [
    { id: "gemini-3.0-pro", label: "Gemini 3.0 Pro" },
    { id: "gemini-3.1-pro", label: "Gemini 3.1 Pro" },
    { id: "gemini-3.0-flash", label: "Gemini 3.0 Flash" },
  ],
};

const FAMILY_PROVIDER_MAP: Record<ModelFamily, ProviderId> = {
  sonnet: "anthropic",
  codex: "openai",
  gemini: "google",
};

const FAMILY_DEFAULT_MODELS: Record<
  ModelFamily,
  {
    answer: string;
    parse: string;
    utility: string;
  }
> = {
  sonnet: {
    answer: "claude-sonnet-4-6",
    parse: "claude-sonnet-4-6",
    utility: "claude-haiku-4-5-20251001",
  },
  codex: {
    answer: "gpt-5.2",
    parse: "gpt-5.2",
    utility: "gpt-4o",
  },
  gemini: {
    answer: "gemini-3.1-pro",
    parse: "gemini-3.1-pro",
    utility: "gemini-3.0-flash",
  },
};

const VALID_FAMILIES = new Set<ModelFamily>(["sonnet", "codex", "gemini"]);

export function getProviderForFamily(family: ModelFamily): ProviderId {
  return FAMILY_PROVIDER_MAP[family];
}

export function getAnswerModelId(family: ModelFamily): string {
  return FAMILY_DEFAULT_MODELS[family].answer;
}

export function getParseModelId(family: ModelFamily): string {
  return FAMILY_DEFAULT_MODELS[family].parse;
}

export function getUtilityModelId(family: ModelFamily): string {
  return FAMILY_DEFAULT_MODELS[family].utility;
}

export function resolveUtilityFamily(config: ModelConfig, taskFamily: ModelFamily): ModelFamily {
  return config.utilitiesOverrideFamily ?? taskFamily;
}

export function isParseFamilySupported(family: ModelFamily): boolean {
  return family === "sonnet";
}

export function getParseUnsupportedReason(family: ModelFamily): string {
  if (isParseFamilySupported(family)) return "";
  return `${FAMILY_PROVIDER_MAP[family]} parsing is not yet supported for PDF + structured extraction. Use Sonnet for parsing.`;
}

export function familyFromModelId(modelId: string | undefined): ModelFamily | null {
  if (!modelId) return null;
  for (const family of Object.keys(APPROVED_MODEL_CATALOG) as ModelFamily[]) {
    if (APPROVED_MODEL_CATALOG[family].some((model) => model.id === modelId)) {
      return family;
    }
  }
  return null;
}

export function normalizeModelConfig(raw: unknown): ModelConfig {
  const input = (raw ?? {}) as Partial<ModelConfig> & {
    primaryModel?: string;
    fastModel?: string;
  };

  // Migration path from old shape
  if (typeof input.primaryModel === "string" || typeof input.fastModel === "string") {
    const primaryFamily = familyFromModelId(input.primaryModel) ?? DEFAULT_MODEL_CONFIG.answerFamily;
    const fastFamily = familyFromModelId(input.fastModel);

    return {
      answerFamily: primaryFamily,
      parseFamily: primaryFamily,
      utilitiesOverrideFamily: fastFamily ?? null,
    };
  }

  const answerFamily = VALID_FAMILIES.has(input.answerFamily as ModelFamily)
    ? (input.answerFamily as ModelFamily)
    : DEFAULT_MODEL_CONFIG.answerFamily;

  const parseFamily = VALID_FAMILIES.has(input.parseFamily as ModelFamily)
    ? (input.parseFamily as ModelFamily)
    : DEFAULT_MODEL_CONFIG.parseFamily;

  const utilitiesOverrideFamily = VALID_FAMILIES.has(input.utilitiesOverrideFamily as ModelFamily)
    ? (input.utilitiesOverrideFamily as ModelFamily)
    : null;

  return {
    answerFamily,
    parseFamily,
    utilitiesOverrideFamily,
  };
}

export function normalizeProviderKeyStatus(raw: Partial<Record<ProviderId, unknown>>): ProviderKeyStatus {
  return {
    anthropic: Boolean(raw.anthropic),
    openai: Boolean(raw.openai),
    google: Boolean(raw.google),
  };
}
